import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, Check, Minus, Plus, Phone, CalendarDays, Clock, MapPin, Loader2, Lock,
} from "lucide-react";
import { API, useConfig, eur, PHONE, PHONE_TEL } from "@/lib/api";

const isoDay = (offset = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const feeFor = (km) => (km <= 10 ? 0 : Math.ceil((km - 10) / 10) * 7);
const ALLOWED_DEPTS = ["69", "26", "01", "71", "38", "07"];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export default function Commander() {
  const { config } = useConfig();
  const [params] = useSearchParams();
  const [zone, setZone] = useState(params.get("zone") === "drome" ? "drome" : "lyon");
  const [formuleId, setFormuleId] = useState(params.get("formule") || "signature");
  const [optionIds, setOptionIds] = useState([]);
  const [people, setPeople] = useState(4);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [city, setCity] = useState("");
  const [customCp, setCustomCp] = useState("");
  const [customVille, setCustomVille] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState("");

  const today = isoDay(0);
  const minDate = isoDay(1);
  const sameDay = date !== "" && date <= today;

  useEffect(() => {
    if (!date || sameDay) {
      setAvailability([]);
      return;
    }
    axios
      .get(`${API}/availability`, { params: { date, zone } })
      .then((r) => setAvailability(r.data.slots))
      .catch(() => setAvailability([]));
  }, [date, zone, sameDay]);

  const totals = useMemo(() => {
    if (!config) return null;
    const formule = config.formules.find((f) => f.id === formuleId);
    if (!formule) return null;
    const opts = config.options.filter((o) => optionIds.includes(o.id));
    const cityObj = config.zones[zone].cities.find((c) => c.nom === city);
    const isCustom = city === "__autre__";
    const cpOk = /^\d{5}$/.test(customCp) && ALLOWED_DEPTS.includes(customCp.slice(0, 2));
    const customOk = isCustom && cpOk && customVille.trim().length > 0;
    const sub = formule.prix * people + opts.reduce((s, o) => s + o.prix * (o.unite === "personne" ? people : 1), 0);
    const fee = isCustom ? (customOk ? 15 : null) : (cityObj ? feeFor(cityObj.km) : 0);
    const discount = promo ? Math.round(sub * promo.percent) / 100 : 0;
    return { formule, opts, cityObj, isCustom, customOk, sub, fee, discount, total: fee === null ? null : sub - discount + fee };
  }, [config, formuleId, optionIds, people, zone, city, promo, customCp, customVille]);

  const applyPromo = async () => {
    setPromoError("");
    try {
      const { data } = await axios.post(`${API}/promo/check`, { code: promoInput });
      setPromo(data);
      toast.success(`Code ${data.code} appliqué : −${data.percent} %`);
    } catch {
      setPromo(null);
      setPromoError("Ce code n'est pas valide.");
    }
  };

  if (!config) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center" data-testid="order-loading">
        <Loader2 className="animate-spin text-terracotta" size={32} />
      </div>
    );
  }

  const toggleOption = (id) =>
    setOptionIds((prev) => (prev.includes(id) ? prev.filter((o) => o !== id) : [...prev, id]));

  const slotFull = (s) => availability.find((a) => a.slot === s)?.complet;

  const canSubmit =
    !sameDay && date && slot && city && (city !== "__autre__" || totals?.customOk) && contact.name && contact.email && contact.phone && contact.address;

  const submit = async () => {
    if (!canSubmit || loading) return;
    setLoading(true);
    try {
      const isCustom = city === "__autre__";
      const { data } = await axios.post(`${API}/orders/checkout`, {
        zone, formule_id: formuleId, option_ids: optionIds, people, date, slot,
        city: isCustom ? customVille.trim() : city,
        postal_code: isCustom ? customCp : null,
        address: contact.address, name: contact.name, email: contact.email,
        phone: contact.phone, notes: contact.notes, promo_code: promo?.code || null, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "SAME_DAY") {
        toast.error("Pour aujourd'hui, contactez-nous directement au " + PHONE);
      } else {
        toast.error(typeof detail === "string" ? detail : "Une erreur est survenue, réessayez.");
      }
      setLoading(false);
    }
  };

  const field = (key, label, props = {}) => (
    <label className="block">
      <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-clay">{label}</span>
      <input
        data-testid={`order-input-${key}`}
        className="input-elegant"
        value={contact[key]}
        onChange={(e) => setContact({ ...contact, [key]: e.target.value })}
        {...props}
      />
    </label>
  );

  return (
    <div className="mx-auto max-w-7xl px-6 py-16 lg:px-12 lg:py-24" data-testid="order-page">
      <Reveal>
        <p className="eyebrow">Réservation en ligne</p>
        <h1 className="display-hero mt-4 text-4xl sm:text-5xl lg:text-6xl">
          Composez votre <span className="italic text-terracotta">brunch.</span>
        </h1>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-espresso/70">
          Commandez la veille pour le lendemain — paiement sécurisé en ligne, livraison à l'heure dite.
        </p>
      </Reveal>

      <div className="mt-16 grid gap-12 lg:grid-cols-12">
        <div className="space-y-16 lg:col-span-8">
          <Reveal>
            <section data-testid="order-step-zone">
              <h2 className="font-serif text-2xl font-light"><span className="text-terracotta">01 ·</span> Votre zone</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {Object.entries(config.zones).map(([key, z]) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`order-zone-${key}`}
                    onClick={() => { setZone(key); setCity(""); }}
                    className={`rounded-2xl border p-6 text-left transition-[border-color,background-color,transform] duration-300 hover:scale-[1.01] ${
                      zone === key ? "border-terracotta bg-terracotta/5" : "border-sand bg-white/50"
                    }`}
                  >
                    <p className="font-serif text-xl font-light">{z.label}</p>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-clay">
                      <MapPin size={12} strokeWidth={1.5} /> {z.base}
                    </p>
                  </button>
                ))}
              </div>
            </section>
          </Reveal>

          <Reveal>
            <section data-testid="order-step-formule">
              <h2 className="font-serif text-2xl font-light"><span className="text-terracotta">02 ·</span> Votre formule</h2>
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {config.formules.filter((f) => !f.devis).map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    data-testid={`order-formule-${f.id}`}
                    onClick={() => setFormuleId(f.id)}
                    className={`flex flex-col rounded-2xl border p-6 text-left transition-[border-color,background-color,transform] duration-300 hover:scale-[1.01] ${
                      formuleId === f.id ? "border-terracotta bg-terracotta/5" : "border-sand bg-white/50"
                    }`}
                  >
                    <div className="flex items-baseline justify-between">
                      <p className="font-serif text-2xl font-light">{f.nom}</p>
                      {formuleId === f.id && <Check size={18} className="text-terracotta" />}
                    </div>
                    {f.featured && (
                      <span className="mt-2 inline-block self-start rounded-full bg-terracotta px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-cream">
                        Notre formule préférée
                      </span>
                    )}
                    <p className="mt-1 text-lg font-semibold text-terracotta">{f.prefix || ""}{eur(f.prix)} <span className="text-xs font-normal text-clay">/ pers.</span></p>
                    <p className="mt-2 text-xs italic text-clay">{f.tagline}</p>
                  </button>
                ))}
              </div>
              <p className="mt-4 text-sm italic text-clay" data-testid="order-grazing-note">
                Envie d'une Grazing Table ? Elle se compose sur devis —{" "}
                <a href="/devis?formule=grazing" data-testid="order-grazing-devis-link" className="font-semibold text-terracotta not-italic underline underline-offset-4">demandez votre proposition</a>.
              </p>

              {totals?.formule && (
                <div className="mt-8 rounded-2xl border border-sand bg-white/50 p-7" data-testid="order-composition">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-clay">
                    Composition — {totals.formule.nom}
                  </p>
                  <ul className="mt-4 grid gap-x-8 gap-y-2 text-sm leading-relaxed text-espresso/75 sm:grid-cols-2">
                    {totals.formule.items.map((it) => (
                      <li key={it} className="flex gap-2">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-terracotta" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-8">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-clay">Options gourmandes</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {config.options.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      data-testid={`order-option-${o.id}`}
                      onClick={() => toggleOption(o.id)}
                      className={`flex items-center justify-between rounded-xl border px-5 py-4 text-left text-sm transition-[border-color,background-color] duration-300 ${
                        optionIds.includes(o.id) ? "border-terracotta bg-terracotta/5" : "border-sand bg-white/50"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${optionIds.includes(o.id) ? "border-terracotta bg-terracotta text-cream" : "border-sand"}`}>
                          {optionIds.includes(o.id) && <Check size={12} />}
                        </span>
                        {o.nom}
                      </span>
                      <span className="font-semibold text-terracotta">
                        +{eur(o.prix)} <span className="text-xs font-normal text-clay">{o.unite === "personne" ? "/pers." : ""}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex items-center gap-6">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-clay">Convives</span>
                <div className="flex items-center gap-4 rounded-full border border-sand bg-white/50 px-3 py-2">
                  <button type="button" data-testid="order-people-minus" onClick={() => setPeople(Math.max(2, people - 1))} className="rounded-full p-2 transition-colors duration-200 hover:bg-sand/60">
                    <Minus size={16} strokeWidth={1.5} />
                  </button>
                  <span data-testid="order-people-count" className="w-10 text-center font-serif text-2xl font-light">{people}</span>
                  <button type="button" data-testid="order-people-plus" onClick={() => setPeople(Math.min(200, people + 1))} className="rounded-full p-2 transition-colors duration-200 hover:bg-sand/60">
                    <Plus size={16} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </section>
          </Reveal>

          <Reveal>
            <section data-testid="order-step-date">
              <h2 className="font-serif text-2xl font-light"><span className="text-terracotta">03 ·</span> Date & créneau</h2>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                <label className="block">
                  <span className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-clay">
                    <CalendarDays size={14} strokeWidth={1.5} /> Date de livraison
                  </span>
                  <input
                    type="date"
                    data-testid="order-date-input"
                    min={minDate}
                    value={date}
                    onChange={(e) => { setDate(e.target.value); setSlot(""); }}
                    className="input-elegant"
                  />
                </label>
                <div>
                  <span className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-clay">
                    <Clock size={14} strokeWidth={1.5} /> Créneau
                  </span>
                  <div className="grid grid-cols-1 gap-3">
                    {config.slots.map((s) => (
                      <button
                        key={s}
                        type="button"
                        data-testid={`order-slot-${s.replace(/[^0-9]/g, "-")}`}
                        disabled={!date || sameDay || slotFull(s)}
                        onClick={() => setSlot(s)}
                        className={`rounded-xl border px-5 py-3.5 text-sm transition-[border-color,background-color] duration-300 disabled:opacity-40 ${
                          slot === s ? "border-terracotta bg-terracotta/5 font-semibold" : "border-sand bg-white/50"
                        }`}
                      >
                        {s} {slotFull(s) && <span className="ml-2 text-xs text-clay">— complet</span>}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {sameDay && (
                <div data-testid="order-sameday-alert" className="mt-6 flex flex-col items-start gap-4 rounded-2xl border border-terracotta/50 bg-terracotta/10 p-6 md:flex-row md:items-center md:justify-between">
                  <p className="text-sm leading-relaxed text-espresso">
                    <strong>Pour aujourd'hui, c'est possible</strong> — mais pas en ligne.
                    Contactez-nous directement pour vérifier nos disponibilités.
                  </p>
                  <a href={PHONE_TEL} data-testid="order-sameday-phone" className="btn-primary shrink-0 !px-6 !py-3">
                    <Phone size={16} strokeWidth={1.5} /> {PHONE}
                  </a>
                </div>
              )}
              {!sameDay && (
                <p className="mt-4 text-sm italic text-clay">
                  Besoin pour aujourd'hui ? Appelez-nous au{" "}
                  <a href={PHONE_TEL} className="font-semibold text-terracotta not-italic">{PHONE}</a>.
                </p>
              )}
            </section>
          </Reveal>

          <Reveal>
            <section data-testid="order-step-delivery">
              <h2 className="font-serif text-2xl font-light"><span className="text-terracotta">04 ·</span> Livraison</h2>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-clay">Commune</span>
                  <select
                    data-testid="order-city-select"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="input-elegant"
                  >
                    <option value="">Choisissez votre commune…</option>
                    {config.zones[zone].cities.map((c) => (
                      <option key={c.nom} value={c.nom}>
                        {c.nom} — {c.km <= 10 ? "livraison offerte" : `+${feeFor(c.km)} € de livraison`}
                      </option>
                    ))}
                    <option value="__autre__">Autre commune — +15 € de livraison</option>
                  </select>
                </label>
                {city === "__autre__" ? (
                  <div className="grid gap-6 sm:grid-cols-[140px_1fr]">
                    <label className="block">
                      <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-clay">Code postal</span>
                      <input
                        data-testid="order-custom-cp-input"
                        className="input-elegant"
                        inputMode="numeric"
                        maxLength={5}
                        placeholder="26000"
                        value={customCp}
                        onChange={(e) => setCustomCp(e.target.value.replace(/\D/g, ""))}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-clay">Votre commune</span>
                      <input
                        data-testid="order-custom-ville-input"
                        className="input-elegant"
                        placeholder="Nom de votre ville ou village"
                        value={customVille}
                        onChange={(e) => setCustomVille(e.target.value)}
                      />
                    </label>
                  </div>
                ) : (
                  field("address", "Adresse complète", { placeholder: "N°, rue, bâtiment, étage, digicode…" })
                )}
              </div>
              {city === "__autre__" && (
                <div className="mt-6" data-testid="order-custom-address-block">
                  {field("address", "Adresse complète", { placeholder: "N°, rue, bâtiment, étage, digicode…" })}
                  <p className="mt-3 text-xs text-clay" data-testid="order-custom-city-hint">
                    Livraison forfaitaire de 15 € — nous livrons toutes les communes du Rhône (69), de la Drôme (26), de l'Ain (01), de la Saône-et-Loire (71), de l'Isère (38) et de l'Ardèche (07). Au-delà, appelez-nous au {PHONE}.
                  </p>
                  {customCp.length === 5 && !ALLOWED_DEPTS.includes(customCp.slice(0, 2)) && (
                    <p className="mt-2 text-xs font-semibold text-terracotta" data-testid="order-custom-city-error">
                      Ce département est trop éloigné de nos ateliers — contactez-nous au {PHONE} pour une livraison sur mesure.
                    </p>
                  )}
                </div>
              )}
            </section>
          </Reveal>

          <Reveal>
            <section data-testid="order-step-contact">
              <h2 className="font-serif text-2xl font-light"><span className="text-terracotta">05 ·</span> Vos coordonnées</h2>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                {field("name", "Nom & prénom", { placeholder: "Camille Martin" })}
                {field("email", "Email", { type: "email", placeholder: "camille@exemple.fr" })}
                {field("phone", "Téléphone", { type: "tel", placeholder: "06 12 34 56 78" })}
                <label className="block">
                  <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-clay">Précisions (allergies, envies…)</span>
                  <textarea
                    data-testid="order-input-notes"
                    rows={3}
                    className="input-elegant resize-none"
                    value={contact.notes}
                    onChange={(e) => setContact({ ...contact, notes: e.target.value })}
                    placeholder="Sans gluten, anniversaire, étage sans ascenseur…"
                  />
                </label>
              </div>
            </section>
          </Reveal>
        </div>

        <div className="lg:col-span-4">
          <div className="sticky top-28">
            <Reveal>
              <aside data-testid="order-recap" className="rounded-2xl border border-sand bg-espresso p-8 text-cream">
                <h3 className="font-serif text-2xl font-light">Votre brunch</h3>
                <div className="mt-6 space-y-3 text-sm">
                  <div className="flex justify-between gap-4">
                    <span className="text-cream/60">{totals?.formule.nom} × {people}</span>
                    <span>{eur((totals?.formule.prix || 0) * people)}</span>
                  </div>
                  {totals?.opts.map((o) => (
                    <div key={o.id} className="flex justify-between gap-4">
                      <span className="text-cream/60">{o.nom}{o.unite === "personne" ? ` × ${people}` : ""}</span>
                      <span>{eur(o.prix * (o.unite === "personne" ? people : 1))}</span>
                    </div>
                  ))}
                  <div className="flex justify-between gap-4 border-t border-cream/15 pt-3">
                    <span className="text-cream/60">Livraison {totals?.cityObj ? `— ${totals.cityObj.nom}` : totals?.isCustom && customVille.trim() ? `— ${customVille.trim()}` : ""}</span>
                    <span data-testid="order-recap-fee">{totals?.cityObj ? (totals.fee === 0 ? "Offerte" : eur(totals.fee)) : totals?.customOk ? eur(totals.fee) : "—"}</span>
                  </div>
                  {promo && totals?.discount > 0 && (
                    <div className="flex justify-between gap-4" data-testid="order-recap-discount">
                      <span className="text-terracotta">Code {promo.code} (−{promo.percent} %)</span>
                      <span className="text-terracotta">−{eur(totals.discount)}</span>
                    </div>
                  )}
                  {date && !sameDay && (
                    <div className="flex justify-between gap-4 text-cream/60">
                      <span>Date</span>
                      <span>{new Date(date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}{slot ? ` · ${slot}` : ""}</span>
                    </div>
                  )}
                </div>
                <div className="mt-5 border-t border-cream/15 pt-5">
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-cream/50">Code promo</label>
                  <div className="mt-2 flex gap-2">
                    <input
                      data-testid="order-promo-input"
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                      placeholder="BIENVENUE15"
                      className="w-full rounded-lg border border-cream/20 bg-cream/10 px-4 py-2.5 text-sm text-cream placeholder:text-cream/30 focus:border-terracotta focus:outline-none"
                    />
                    <button
                      type="button"
                      data-testid="order-promo-apply"
                      onClick={applyPromo}
                      className="shrink-0 rounded-lg bg-terracotta px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-cream transition-colors duration-300 hover:bg-cream hover:text-espresso"
                    >
                      Appliquer
                    </button>
                  </div>
                  {promoError && <p className="mt-2 text-xs text-terracotta" data-testid="order-promo-error">{promoError}</p>}
                </div>
                <div className="mt-6 flex items-baseline justify-between border-t border-cream/15 pt-6">
                  <span className="text-xs font-semibold uppercase tracking-[0.25em] text-cream/60">Total</span>
                  <span data-testid="order-recap-total" className="font-serif text-4xl font-light text-terracotta">{eur(totals?.total || 0)}</span>
                </div>
                <button
                  type="button"
                  data-testid="order-submit-btn"
                  onClick={submit}
                  disabled={!canSubmit || loading}
                  className="btn-primary mt-8 w-full !bg-terracotta hover:!bg-cream hover:!text-espresso"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={14} strokeWidth={1.5} />}
                  {loading ? "Redirection sécurisée…" : "Payer et réserver mon brunch"}
                </button>
                <p className="mt-4 flex items-center justify-center gap-2 text-center text-xs text-cream/50">
                  <Lock size={12} strokeWidth={1.5} /> Paiement sécurisé par carte bancaire
                </p>
                <p data-testid="order-recap-confirm-note" className="mt-2 text-center text-xs text-cream/60">
                  Votre créneau est bloqué dès le paiement confirmé — réservation définitive, sans appel.
                </p>
                {!canSubmit && !loading && (
                  <p className="mt-3 text-center text-xs text-cream/40">
                    Complétez la date, le créneau, la commune et vos coordonnées.
                  </p>
                )}
              </aside>
            </Reveal>
          </div>
        </div>
      </div>

      <Reveal className="mt-20">
        <div className="flex flex-col items-start gap-4 rounded-2xl border border-sand bg-white/50 p-8 md:flex-row md:items-center md:justify-between">
          <p className="text-sm leading-relaxed text-espresso/70">
            Une envie qui n'est pas dans nos formules ? Composons ensemble votre brunch idéal.
          </p>
          <a href="/devis" data-testid="order-devis-link" className="btn-ghost shrink-0 !px-6 !py-3">
            Devis sur mesure <ArrowRight size={14} strokeWidth={1.5} />
          </a>
        </div>
      </Reveal>
    </div>
  );
}
